import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { Container } from 'react-bootstrap';
import { Meteor } from 'meteor/meteor';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Accounts } from 'meteor/accounts-base';
import { Switch, Route } from 'react-router-dom';
import { Roles } from 'meteor/alanning:roles';
import { Bert } from 'meteor/themeteorchef:bert';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

import Navigation from '../../../ui/components/Navigation';

import Public from '../../../ui/components/Public';
import Authenticated from '../../../ui/components/Authenticated';
import Authorized from '../../../ui/components/Authorized';
import EmailNotVerified from '../../../ui/components/EmailNotVerified';

import Home from '../../../ui/pages/Home';

import Profile from '../../../ui/pages/Profile';
import SignUp from '../../../ui/pages/SignUp';
import Login from '../../../ui/pages/Login';
import Logout from '../../../ui/pages/Logout';

import RecoverPassword from '../../../ui/pages/RecoverPassword';
import ResetPassword from '../../../ui/pages/ResetPassword';
import VerifyEmail from '../../../ui/pages/VerifyEmail';

import AdminUsers from '../../../ui/pages/AdminUsers';
import AdminUser from '../../../ui/pages/AdminUser';
import AdminUserSettings from '../../../ui/pages/AdminUserSettings';

import AnnotationTemplatesContainer from '../../../ui/containers/AnnotationTemplatesContainer';
import NewAnnotationTemplate from '../../../ui/pages/NewAnnotationTemplate';
import EditAnnotationTemplate from '../../../ui/pages/EditAnnotationTemplate';

import UserManagement from '../../../ui/pages/UserManagement';
import NewUser from '../../../ui/pages/NewUser';
import EditUser from '../../../ui/pages/EditUser';
import UserControl from '../../../api/UserControl/UserControl';

import Terms from '../../../ui/pages/Terms';
import Privacy from '../../../ui/pages/Privacy';
import ExamplePage from '../../../ui/pages/ExamplePage';

import TableListContainer from '../../../ui/containers/TableListContainer';

import Projects from '../../../ui/pages/Projects';
import NewProject from '../../../ui/pages/NewProject';
import ViewVideoProject from '../../../ui/pages/ViewVideoProject';
import PublicVideoProject from '../../../ui/pages/PublicVideoProject';
import Dashboard from '../../../ui/pages/Dashboard';
import EditProject from '../../../ui/pages/EditProject';

import NewVideo from '../../../ui/pages/NewVideo';
import ViewVideo from '../../../ui/pages/ViewVideo';
import PublicVideo from '../../../ui/pages/PublicVideo';
import EditVideo from '../../../ui/pages/EditVideo';

import NotFound from '../../../ui/pages/NotFound';
import Footer from '../../../ui/components/Footer';
import GDPRConsentModal from '../../../ui/components/GDPRConsentModal';

import { getUserName, getEmailAddress, isEmailVerified } from '../../../api/Users/user-operations';
import methodPromise from '../../../modules/method-promise-wrapper';
import { DEFAULT_DEPLOY_CONFIG } from '../../../api/UserSettings/UserSettings';
import { onLogin, onLogout } from '../../../modules/redux/actions';
import withTrackerSsr from '../../../modules/with-tracker-ssr';

library.add(fas, far, fab);
const { deployID } = Meteor.settings.public;
class App extends Component {
  state = {
    deployEnv: '',
    ready: false,
    afterLoginPath: null,
    deployConfig: {}
  };

  componentDidMount() {
    this.getDeplyEnv();
    this.getDeployConfig();
    const { handleOnLogin, handleOnLogout } = this.props;
    Accounts.onLogin(() => handleOnLogin());
    Accounts.onLogout(() => handleOnLogout());
    this.setPageReady();
  }

  async getDeplyEnv() {
    try {
      const deployEnv = await methodPromise('app.getDevEnvironment');
      this.setState({ deployEnv });
    } catch (error) {
      Bert.alert('Could Not Get Dev Environment', 'danger');
    }
  }

  getDeployConfig = async () => {
    let DEPLOY_CONFIG_API = '';
    try {
      const APISettings = await methodPromise('admin.fetchAPISetting');
      if (APISettings.length > 0) {
        DEPLOY_CONFIG_API = APISettings[0].deployConfigAPI;
      }
    } catch (error) {
      // handleMethodException(error);
    }
    try {
      const response = await fetch(DEPLOY_CONFIG_API, {
        method: 'POST',
        body: JSON.stringify({ user: deployID }),
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });
      const data = await response.json();
      this.setState({ deployConfig: data.GMV });
    } catch (error) {
      this.setState({ deployConfig: DEFAULT_DEPLOY_CONFIG.GMV });
    }
  };

  setPageReady = () => {
    this.setState({ ready: true });
  };

  setAfterLoginPath = afterLoginPath => {
    this.setState({ afterLoginPath });
  };

  render() {
    const { props, state, setAfterLoginPath } = this;
    const isAppReady = state.ready && !props.loading && state.deployEnv !== '';
    return (
      <div className={isAppReady ? 'visible' : 'invisible'}>
        {props.authenticated && <GDPRConsentModal userId={props.userId} />}
        <Navigation {...props} {...state} />
        <Container fluid>
          <Switch>
            <Route
              exact
              name="home"
              path="/"
              render={routeProps => <Home {...routeProps} {...state} {...props} />}
            />
            <Authenticated
              exact
              path="/projects"
              setAfterLoginPath={setAfterLoginPath}
              component={TableListContainer}
              Page={Projects}
              {...props}
              {...state}
            />
            <Authenticated
              exact
              path="/projects/new"
              component={NewProject}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authenticated
              exact
              path="/projects/:project_id/edit"
              component={EditProject}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authenticated
              exact
              path="/projects/:project_id"
              component={ViewVideoProject}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authenticated
              exact
              path="/projects/:project_id/newVideo"
              component={NewVideo}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authenticated
              exact
              path="/projects/:project_id/videos/:video_id/edit"
              component={EditVideo}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authenticated
              exact
              path="/sharedPrivate/videos/:video_id"
              component={PublicVideo}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Route
              exact
              path="/projects/:project_id/videos/:video_id"
              render={routeProps => (
                <ViewVideo
                  setAfterLoginPath={setAfterLoginPath}
                  {...props}
                  {...state}
                  {...routeProps}
                />
              )}
            />
            <EmailNotVerified
              exact
              path="/profile"
              component={Profile}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Public path="/signup" component={SignUp} {...props} {...state} />
            <Public path="/login" component={Login} {...props} {...state} />
            <Route
              path="/dashboard"
              render={routeProps => (
                <Dashboard
                  setAfterLoginPath={setAfterLoginPath}
                  {...props}
                  {...state}
                  {...routeProps}
                />
              )}
            />
            <Route
              path="/shared/videos/:video_id"
              render={routeProps => (
                <PublicVideo
                  setAfterLoginPath={setAfterLoginPath}
                  {...props}
                  {...state}
                  {...routeProps}
                />
              )}
            />
            <Route
              path="/shared/projects/:project_id"
              render={routeProps => (
                <PublicVideoProject
                  setAfterLoginPath={setAfterLoginPath}
                  {...props}
                  {...state}
                  {...routeProps}
                />
              )}
            />
            <Route
              path="/logout"
              render={routeProps => (
                <Logout
                  {...routeProps}
                  {...props}
                  {...state}
                  setAfterLoginPath={setAfterLoginPath}
                />
              )}
            />
            <Route name="verify-email" path="/verify-email/:token" component={VerifyEmail} />
            <Route name="recover-password" path="/recover-password" component={RecoverPassword} />
            <Route name="reset-password" path="/reset-password/:token" component={ResetPassword} />
            <Route name="terms" path="/terms" component={Terms} />
            <Route name="privacy" path="/privacy" component={Privacy} />
            <Route name="examplePage" path="/example-page" component={ExamplePage} />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/users"
              pathAfterFailure="/"
              component={UserManagement}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/users/new"
              pathAfterFailure="/"
              component={NewUser}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/users/:user_id/edit"
              pathAfterFailure="/"
              component={EditUser}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/admin/users"
              pathAfterFailure="/"
              component={AdminUsers}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/admin/users/settings"
              pathAfterFailure="/"
              component={AdminUserSettings}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/admin/users/:_id"
              pathAfterFailure="/"
              component={AdminUser}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/admin/annotationtemplates"
              pathAfterFailure="/"
              component={AnnotationTemplatesContainer}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/admin/annotationtemplates/new"
              pathAfterFailure="/"
              component={NewAnnotationTemplate}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Authorized
              exact
              allowedRoles={['admin']}
              path="/admin/annotationtemplates/:template_id/edit"
              pathAfterFailure="/"
              component={EditAnnotationTemplate}
              setAfterLoginPath={setAfterLoginPath}
              {...props}
              {...state}
            />
            <Route component={NotFound} />
          </Switch>
        </Container>
        <Footer />
      </div>
    );
  }
}

App.defaultProps = {
  userId: '',
  emailAddress: '',
  isPremium: false,
  userControlSetting: {}
};

App.propTypes = {
  authenticated: PropTypes.bool.isRequired,
  emailAddress: PropTypes.string,
  emailVerified: PropTypes.bool.isRequired,
  isPremium: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  userId: PropTypes.string,
  handleOnLogin: PropTypes.func.isRequired,
  handleOnLogout: PropTypes.func.isRequired,
  userControlSetting: PropTypes.object
};

const mapStateToProps = state => ({ ...state });
const mapDispatchToProps = dispatch => ({
  handleOnLogin: data => dispatch(onLogin(data)),
  handleOnLogout: data => dispatch(onLogout(data))
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTrackerSsr(() => {
    const app = Meteor.subscribe('app');
    const loggingIn = Meteor.loggingIn();
    const userControlSub = Meteor.subscribe('UserControl');
    const userSettings = UserControl.find({}).fetch();
    const loading = !app.ready() && !Roles.subscription.ready() && !userControlSub.ready();
    const user = Meteor.user();
    const userId = Meteor.userId();
    if (isNil(user)) {
      return {
        authenticated: !loggingIn && !!userId,
        emailAddress: '',
        emailVerified: false,
        isAdmin: Roles.userIsInRole(userId, 'admin'),
        isPremium: Roles.userIsInRole(userId, 'premium-user'),
        isBasic: Roles.userIsInRole(userId, 'basic-user'),
        isFree: Roles.userIsInRole(userId, 'free-user'),
        loading,
        loggingIn,
        name: '',
        roles: !loading && Roles.getRolesForUser(userId),
        userId,
        userControlSetting: userSettings[0]
      };
    }
    const name = getUserName(user);
    const emailAddress = getEmailAddress(user);
    const emailVerified = isEmailVerified(user);
    return {
      authenticated: !loggingIn && !!userId,
      emailAddress,
      emailVerified,
      isAdmin: Roles.userIsInRole(userId, 'admin'),
      isPremium: Roles.userIsInRole(userId, 'premium-user'),
      isBasic: Roles.userIsInRole(userId, 'basic-user'),
      isFree: Roles.userIsInRole(userId, 'free-user'),
      loading,
      loggingIn,
      name: name || emailAddress,
      roles: !loading && Roles.getRolesForUser(userId),
      userId,
      userControlSetting: userSettings[0]
    };
  })
)(App);
